import React, { useEffect } from 'react';
import type { PlanProgress, ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import {
  ArrowPathIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';

import { useTranslation } from 'react-i18next';

interface PlanStep {
  title: string;
  description: string;
}

interface PlanInput {
  success?: boolean;
  steps?: unknown;
  message?: string;
}

function isPlanStep(value: unknown): value is PlanStep {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return typeof obj.title === 'string' && typeof obj.description === 'string';
}

function getPlanSteps(input: PlanInput): PlanStep[] {
  if (!Array.isArray(input.steps)) {
    return [];
  }
  return input.steps.filter(isPlanStep);
}

function toPlanInput(raw: object): PlanInput {
  const out: PlanInput = {};
  if ('message' in raw && typeof raw.message === 'string') {
    out.message = raw.message;
  }
  if ('steps' in raw && Array.isArray(raw.steps)) {
    out.steps = raw.steps;
  }
  if ('success' in raw && typeof raw.success === 'boolean') {
    out.success = raw.success;
  }
  return out;
}

interface PlanContentProps {
  content: ToolUseBlock;
  /** Message id of this block; used to register this plan so updatePlanProgress blocks apply to it and to read progress from the store. */
  messageId?: string;
  /** Override progress (e.g. for Storybook). When set, store is not used. */
  planProgress?: PlanProgress;
}

/**
 * Renders the presentPlan tool block: plan title, optional message/note, and a list of steps with checkmarks or in-progress spinners.
 *
 * Flow:
 * - On mount we call setPresentPlan(conversationId, messageId). That sets lastPresentPlanMessageId so any
 *   updatePlanProgress blocks later in the message list apply to this plan.
 * - Progress (completedStepIndices, inProgressStepIndices, skippedStepIndices, lastNote) comes from the store, keyed by this messageId.
 * - "message" is from the presentPlan input; "lastNote" is the latest note from updatePlanProgress (e.g. "Created PROJ-123").
 */
export const PlanContent: React.FC<PlanContentProps> = ({
  content,
  messageId,
  planProgress: planProgressOverride,
}) => {
  const { t } = useTranslation('pleri/chat');
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const setPresentPlan = useChatStore((state) => state.setPresentPlan);
  const planProgressFromStore = useChatStore((state) => {
    if (!activeConversationId || !messageId) {
      return undefined;
    }
    const conv = state.conversations[activeConversationId];
    return conv?.planProgressByPlanMessageId?.[messageId];
  });
  useEffect(() => {
    if (
      planProgressOverride === undefined &&
      activeConversationId &&
      messageId
    ) {
      setPresentPlan(activeConversationId, messageId);
    }
  }, [activeConversationId, messageId, planProgressOverride, setPresentPlan]);

  const planProgress =
    planProgressOverride !== undefined
      ? planProgressOverride
      : planProgressFromStore;

  const input: PlanInput =
    typeof content.input === 'object' && content.input !== null
      ? toPlanInput(content.input)
      : {};

  const steps = getPlanSteps(input);
  const message = typeof input.message === 'string' ? input.message : undefined;

  if (steps.length === 0) {
    return null;
  }

  const completedSet = new Set(planProgress?.completedStepIndices ?? []);
  const inProgressSet = new Set(planProgress?.inProgressStepIndices ?? []);
  const skippedSet = new Set(planProgress?.skippedStepIndices ?? []);
  const lastNote = planProgress?.lastNote; // Latest note from updatePlanProgress (not from presentPlan message)

  const planCard = (
    <div className="rounded-lg bg-white p-4 dark:bg-gray-900">
      <section
        aria-labelledby="plan-heading"
        className="border-b-2 border-orange-200 pb-3 dark:border-gray-600"
      >
        <div className="flex min-w-0 items-center gap-3 text-sm">
          <div className="bg-action-primary text-action-primary-text flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full">
            <ClipboardDocumentListIcon className="h-3 w-3" />
          </div>
          <div className="flex min-w-0 flex-1 items-center overflow-hidden">
            <h3
              id="plan-heading"
              className="text-darkest-gray shrink-0 font-semibold decoration-gray-300 underline-offset-2"
            >
              {t('plan')}
            </h3>
            {(message || lastNote) && (
              <span
                className="text-darker-gray min-w-0 truncate pl-1.5"
                title={[message, lastNote].filter(Boolean).join(' · ')}
              >
                {message && (
                  <>
                    <span className="shrink-0"> · </span>
                    {message}
                  </>
                )}
                {lastNote && (
                  <>
                    <span className="shrink-0"> · </span>
                    <span className="text-darkest-gray text-xs opacity-80">
                      {lastNote}
                    </span>
                  </>
                )}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="relative mt-4">
        <div
          className="absolute top-0 bottom-0 left-[10px] z-0 w-0.5 -translate-x-1/2 bg-gray-300 dark:bg-gray-600"
          aria-hidden
        />
        <ol className="relative z-10 list-none space-y-3 p-0">
          {steps.map((step, index) => {
            const completed = completedSet.has(index);
            const inProgress = inProgressSet.has(index);
            const skipped = skippedSet.has(index);
            return (
              <li key={index} className="flex items-start gap-3">
                <span
                  className={`relative z-10 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-sm ${
                    completed
                      ? 'bg-action-primary text-action-primary-text'
                      : inProgress
                        ? 'bg-action-primary text-action-primary-text'
                        : skipped
                          ? 'bg-lightest-gray text-darker-gray'
                          : 'bg-lightest-gray text-darkest-gray'
                  }`}
                  aria-hidden
                >
                  {completed ? (
                    <CheckIcon className="h-3 w-3" />
                  ) : inProgress ? (
                    <ArrowPathIcon
                      className="h-3 w-3 animate-spin"
                      aria-hidden
                    />
                  ) : skipped ? (
                    <NoSymbolIcon className="h-3 w-3" aria-hidden />
                  ) : (
                    index + 1
                  )}
                </span>
                <div className="text-darkest-gray min-w-0 flex-1 truncate text-sm">
                  <span
                    className={
                      completed
                        ? 'line-through'
                        : skipped
                          ? 'text-darker-gray italic'
                          : ''
                    }
                  >
                    {step.title}
                  </span>
                  {skipped && (
                    <span className="text-darker-gray ml-2 text-xs">
                      {t('skipped')}
                    </span>
                  )}
                  <span className="ml-2 text-xs opacity-80">
                    {step.description}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );

  return <div className="my-3">{planCard}</div>;
};
